import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcArrowDown, IcPlus, IcTrash, IcPdf, IcCheckCircle } from "../../Icons";
import axios from "axios";
import { Button, Table, Input, Label } from "reactstrap";
import Header from "../../layout/Header/Header";
import Swal from "sweetalert2";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeader, pdfFooter, pdfTableStyles } from "../../../pdfUtils";

const fmt = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(v) || 0);

function ListadoEntradas() {
  const { user } = useContext(AuthContext);
  const URL_ENTRADAS = process.env.REACT_APP_URL_ENTRADAS;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const auth = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  const [entradas, setEntradas] = useState([]);
  const [fechaInicio, setFechaInicio] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [fechaFin, setFechaFin] = useState(moment().format("YYYY-MM-DD"));
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const cargar = () => {
    axios
      .post(`${URL_ENTRADAS}/fechas`,
        { selectedFechaInicio: fechaInicio, selectedFechaFin: fechaFin }, auth)
      .then((response) => setEntradas(response.data))
      .catch(() => {});
  };

  useEffect(() => { cargar(); }, [fechaInicio, fechaFin]); // eslint-disable-line

  // Al abrir, la vista se para en el último mes CON movimiento en lugar del mes en curso,
  // para no arrancar en blanco cuando se captura con rezago o el día 1 del mes.
  useEffect(() => {
    axios
      .post(`${URL_ENTRADAS}/fechas`, {
        selectedFechaInicio: moment().subtract(12, "months").startOf("month").format("YYYY-MM-DD"),
        selectedFechaFin: moment().format("YYYY-MM-DD"),
      }, auth)
      .then((response) => {
        const ultima = response.data.map((a) => a.fecha).sort().pop();
        if (ultima && ultima < fechaInicio) {
          setFechaInicio(moment(ultima).startOf("month").format("YYYY-MM-DD"));
          setFechaFin(moment(ultima).endOf("month").format("YYYY-MM-DD"));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  function cambiaStatus(a) {
    const nuevo = a.status === "Pendiente" ? "Cobrado" : "Pendiente";
    axios
      .patch(`${URL_ENTRADAS}/${a._id}`, { status: nuevo }, auth)
      .then(() => {
        axios.post(URL_LOGS, {
          tipo: "Cambio Status Entrada", detalle: `${a.idEntrada} ${nuevo}`, user: user.id,
        }, auth);
        cargar();
      })
      .catch(() => Swal.fire({ icon: "error", title: "Oops...", text: "Something went wrong!" }));
  }

  function eliminar(a) {
    Swal.fire({
      title: "¿Dar de baja la entrada?",
      text: "Se revierte el saldo del cliente",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, dar de baja",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios
        .delete(`${URL_ENTRADAS}/${a._id}`, auth)
        .then(() => {
          axios.post(URL_LOGS, {
            tipo: "Baja Entrada", detalle: `${a.idEntrada}`, user: user.id,
          }, auth);
          Swal.fire("¡Listo!", "Entrada dada de baja", "success");
          cargar();
        })
        .catch(() => Swal.fire({ icon: "error", title: "Oops...", text: "Something went wrong!" }));
    });
  }

  const filtradas = entradas
    .filter((a) => filtroCliente === "" ||
      (a.clientes?.[0]?.nombre_comercial || "").toLowerCase().includes(filtroCliente.toLowerCase()))
    .filter((a) => filtroStatus === "" || a.status === filtroStatus);

  const totalMonto = filtradas.reduce((s, a) => s + (a.monto || 0), 0);
  const totalComision = filtradas.reduce((s, a) => s + (a.comisionTotal || 0), 0);
  const totalRestante = filtradas.reduce((s, a) => s + (a.restante || 0), 0);

  function generaPDF() {
    const doc = new jsPDF({ orientation: "landscape" });
    const startY = pdfHeader(doc, "Listado de Entradas", `${fechaInicio} al ${fechaFin}`);

    doc.autoTable({
      startY,
      head: [["#", "Fecha", "Categoría", "Empresa", "Cliente", "Monto", "Com. Total", "Restante", "Estatus"]],
      body: filtradas.map((a) => [
        a.idEntrada,
        a.fecha,
        a.categorias?.[0]?.name || "",
        a.empresas?.[0]?.name || "",
        a.clientes?.[0]?.nombre_comercial || "",
        fmt(a.monto),
        fmt(a.comisionTotal),
        fmt(a.restante),
        a.status,
      ]),
      foot: [["", "", "", "", "Total", fmt(totalMonto), fmt(totalComision), fmt(totalRestante), ""]],
      ...pdfTableStyles(),
    });

    pdfFooter(doc);
    doc.save(`Entradas_${fechaInicio}_${fechaFin}.pdf`);
  }

  return (
    <>
      <Header />
      {user.menu_entradas ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <IcArrowDown style={{ marginRight: 8, color: "#10b981" }} />
                Entradas
              </h3>
              <p className="page-subtitle">Operaciones que suman saldo al cliente</p>
            </div>
            <div className="page-actions">
              <Button color="danger" size="sm" onClick={generaPDF}>
                <IcPdf style={{ marginRight: 6 }} />
                PDF
              </Button>{" "}
              <Button color="success" size="sm" href="/EntradasCreate">
                <IcPlus style={{ marginRight: 6 }} />
                Nueva Entrada
              </Button>
            </div>
          </div>

          <div className="filter-bar">
            <div>
              <Label>Fecha inicio</Label>
              <Input bsSize="sm" type="date" value={fechaInicio}
                     onChange={(e) => setFechaInicio(e.target.value)} />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input bsSize="sm" type="date" value={fechaFin}
                     onChange={(e) => setFechaFin(e.target.value)} />
            </div>
            <div>
              <Label>Cliente</Label>
              <Input bsSize="sm" type="text" placeholder="Buscar..." value={filtroCliente}
                     onChange={(e) => setFiltroCliente(e.target.value)} />
            </div>
            <div>
              <Label>Estatus</Label>
              <Input bsSize="sm" type="select" value={filtroStatus}
                     onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Cobrado">Cobrado</option>
              </Input>
            </div>
          </div>

          <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
            <thead>
              <tr>
                <th>#</th>
                <th>Fecha</th>
                <th>Categoría</th>
                <th>Empresa</th>
                <th>Cliente</th>
                <th style={{ textAlign: "right" }}>Regresar</th>
                <th style={{ textAlign: "right" }}>TC</th>
                <th style={{ textAlign: "right" }}>Monto</th>
                <th style={{ textAlign: "right" }}>Com. Total</th>
                <th style={{ textAlign: "right" }}>Restante</th>
                <th>Estatus</th>
                <th>Baja</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((a) => (
                <tr key={a._id}>
                  <td>{a.idEntrada}</td>
                  <td>{a.fecha}</td>
                  <td>{a.categorias?.[0]?.name}</td>
                  <td>{a.empresas?.[0]?.name}</td>
                  <td>{a.clientes?.[0]?.nombre_comercial}</td>
                  <td style={{ textAlign: "right" }}>
                    {a.formato === "Transferencia"
                      ? `${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(a.regresar || 0)}`
                      : fmt(a.regresar)}
                  </td>
                  <td style={{ textAlign: "right" }}>{a.tc ? a.tc : "-"}</td>
                  <td style={{ textAlign: "right" }}>{fmt(a.monto)}</td>
                  <td style={{ textAlign: "right" }}>{fmt(a.comisionTotal)}</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(a.restante)}</td>
                  <td>
                    <Button
                      size="sm"
                      color={a.status === "Cobrado" ? "success" : "warning"}
                      onClick={() => cambiaStatus(a)}
                      title="Cambiar estatus"
                    >
                      <IcCheckCircle style={{ marginRight: 4 }} />
                      {a.status}
                    </Button>
                  </td>
                  <td>
                    <Button color="danger" size="sm" onClick={() => eliminar(a)}>
                      <IcTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ textAlign: "right", fontWeight: 700 }}>Totales</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(totalMonto)}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(totalComision)}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(totalRestante)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoEntradas;
